import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import { FiMaximize2, FiMinimize2, FiMinus, FiPlus } from 'react-icons/fi';
import CustomSelect from '../../../partials/Form/Select/CustomSelect';
import { formSelect } from '../../../partials/Form/Select/styles';
import {
  buildTimelineGrid,
  filterTimelineStudies,
  formatTimelineMonthLabel
} from '../utils/dashboardData';
import styles from '../Dashboard.module.scss';

const timelineSelectStyles = {
  ...formSelect,
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

function getStepColor(step, planningOptions = []) {
  const found = planningOptions.find(item => item.value === step.name);
  return found?.color || '#7DDE92';
}

function getStepLayout(step, grid) {
  const startDate = new Date(step.startDate);
  const endDate = new Date(step.endDate);
  const left = differenceInCalendarDays(startDate, grid.minDate) * grid.dayWidth;
  const width = Math.max(
    grid.dayWidth,
    differenceInCalendarDays(endDate, startDate) * grid.dayWidth
  );
  return { left, width };
}

export default function StudiesTimelineTile({ studies = [], optionsLists, isExpanded, onToggleExpand }) {
  const timelineRef = useRef(null);
  const horizontalScrollRef = useRef(null);
  const [search, setSearch] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [monthZoom, setMonthZoom] = useState(1);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const filteredStudies = useMemo(() => filterTimelineStudies(studies, {
    search,
    types: selectedTypes,
    therapeuticArea: selectedArea
  }), [studies, search, selectedTypes, selectedArea]);

  const grid = useMemo(() => buildTimelineGrid(filteredStudies, monthZoom), [filteredStudies, monthZoom]);

  const todayOffset = useMemo(() => {
    if (!grid) return null;
    return differenceInCalendarDays(new Date(), grid.minDate) * grid.dayWidth;
  }, [grid]);

  useEffect(() => {
    if (!isExpanded) setMonthZoom(1);
  }, [isExpanded]);

  useLayoutEffect(() => {
    if (!grid || todayOffset === null || !horizontalScrollRef.current) return;

    const container = horizontalScrollRef.current;
    const scrollToToday = () => {
      const targetScrollLeft = todayOffset - container.clientWidth / 2;
      container.scrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - container.clientWidth));
    };

    scrollToToday();
    requestAnimationFrame(scrollToToday);
  }, [grid, todayOffset, filteredStudies.length, monthZoom, isExpanded]);

  const rowHeight = isExpanded ? 52 : 38;
  const studiesHeight = grid ? grid.timelineStudies.length * rowHeight : 0;

  function handleTypeChange(value) {
    if (!value) {
      setSelectedTypes([]);
      return;
    }
    setSelectedTypes(Array.isArray(value) ? value.map(item => item.value) : [value.value]);
  }

  function handleAreaChange(value) {
    setSelectedArea(value ? value.value : null);
  }

  function handleStepHover(step, event) {
    setHoveredStep(step);
    setTooltipPos({
      x: event.clientX + 12,
      y: event.clientY + 12
    });
  }

  const typesLabel = selectedTypes.length ? `Types (${selectedTypes.length})` : 'Types';

  return (
    <div className={`${styles.tile} ${styles.timelineTile} ${isExpanded ? styles.timelineExpanded : ''}`}>
      <div className={styles.timelineFilters}>
        <input
          className={styles.timelineSearch}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Recherche"
        />
        <div className={styles.timelineSelect}>
          <CustomSelect
            options={optionsLists?.studyTypes || []}
            isMulti
            isClearable
            placeholder={typesLabel}
            handleChange={handleTypeChange}
            menuPortalTarget={document.body}
            styles={timelineSelectStyles}
          />
        </div>
        <div className={styles.timelineSelect}>
          <CustomSelect
            options={optionsLists?.therapeuticArea || []}
            isClearable
            placeholder="Aire thérapeutique"
            handleChange={handleAreaChange}
            menuPortalTarget={document.body}
            styles={timelineSelectStyles}
          />
        </div>
        <button type="button" className={styles.expandButton} onClick={onToggleExpand} aria-label={isExpanded ? 'Réduire' : 'Agrandir'}>
          {isExpanded ? <FiMinimize2 size={20} /> : <FiMaximize2 size={20} />}
        </button>
      </div>

      <div className={styles.timelineBody}>
        {!grid ? (
          <p className={styles.emptyState}>Aucune étude à afficher sur la timeline</p>
        ) : (
          <div className={styles.timelineChart}>
            <div className={styles.timelineHorizontalScroll} ref={horizontalScrollRef}>
              <div className={styles.timelineChartInner} style={{ width: grid.canvasWidth }}>
                <div className={styles.timelineStudiesScroll} ref={timelineRef}>
                  <div
                    className={styles.timelineStudiesCanvas}
                    style={{ width: grid.canvasWidth, height: studiesHeight }}
                  >
                    <div
                      className={styles.timelineGridLines}
                      style={{ width: grid.canvasWidth, height: studiesHeight }}
                    >
                      {grid.months.map((month, index) => (
                        <div
                          key={`${month.label}-${month.year}-${index}`}
                          className={styles.timelineGridLine}
                          style={{ left: index * grid.monthWidth, width: grid.monthWidth }}
                        />
                      ))}
                      {todayOffset !== null && (
                        <div className={styles.todayLine} style={{ left: todayOffset }} />
                      )}
                    </div>

                    <div className={styles.timelineRows}>
                      {grid.timelineStudies.map((study) => {
                        const left = differenceInCalendarDays(study.timeline.startDate, grid.minDate) * grid.dayWidth;
                        const width = Math.max(
                          grid.dayWidth,
                          differenceInCalendarDays(study.timeline.endDate, study.timeline.startDate) * grid.dayWidth
                        );

                        return (
                          <div key={study._id} className={styles.timelineRow} style={{ height: rowHeight }}>
                            <div
                              className={styles.studyBar}
                              style={{ left, width, top: (rowHeight - 30) / 2 }}
                            >
                              <span>{study.name}</span>
                              {study.timeline.steps.map((step, stepIndex) => {
                                const markerDate = new Date(step.startDate);
                                const markerLeft = differenceInCalendarDays(markerDate, study.timeline.startDate) * grid.dayWidth;
                                return (
                                  <span
                                    key={`${study._id}-marker-${stepIndex}`}
                                    className={styles.stepMarker}
                                    style={{
                                      left: markerLeft,
                                      backgroundColor: getStepColor(step, optionsLists?.planning)
                                    }}
                                  />
                                );
                              })}
                            </div>
                            {study.timeline.steps.map((step, stepIndex) => {
                              const { left: stepLeft, width: stepWidth } = getStepLayout(step, grid);
                              return (
                                <div
                                  key={`${study._id}-step-${stepIndex}`}
                                  className={styles.stepHitArea}
                                  style={{
                                    left: stepLeft,
                                    width: stepWidth,
                                    top: (rowHeight - 30) / 2
                                  }}
                                  onMouseEnter={(event) => handleStepHover(step, event)}
                                  onMouseMove={(event) => handleStepHover(step, event)}
                                  onMouseLeave={() => setHoveredStep(null)}
                                />
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>

                    {hoveredStep && (
                      <div className={styles.timelineTooltip} style={{ left: tooltipPos.x, top: tooltipPos.y }}>
                        <p className={styles.tooltipTitle}>{hoveredStep.name}</p>
                        <p className={styles.tooltipDate}>
                          {format(new Date(hoveredStep.startDate), 'dd.MM.yyyy')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.timelineMonthsFooter} style={{ width: grid.canvasWidth }}>
                  {grid.months.map((month, index) => {
                    const isCurrentMonth = new Date().getMonth() === month.date.getMonth()
                      && new Date().getFullYear() === month.date.getFullYear();
                    return (
                      <div
                        key={`month-label-${index}`}
                        className={`${styles.timelineMonth} ${isCurrentMonth ? styles.timelineMonthActive : ''}`}
                        style={{ width: grid.monthWidth }}
                      >
                        {formatTimelineMonthLabel(month)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isExpanded && grid && (
        <div className={styles.zoomControls}>
          <button type="button" onClick={() => setMonthZoom(value => Math.max(0.5, value - 0.25))} aria-label="Réduire l'échelle des mois">
            <FiMinus size={16} />
          </button>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.25"
            value={monthZoom}
            onChange={(e) => setMonthZoom(parseFloat(e.target.value))}
          />
          <button type="button" onClick={() => setMonthZoom(value => Math.min(2, value + 0.25))} aria-label="Augmenter l'échelle des mois">
            <FiPlus size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
